import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const assistedHatching: ContentBlock<"procedure"> = {
  id: makeId("proc-assisted-hatching"),
  kind: "procedure",
  tags: ["ivf", "lab", "both-partners"],
  title: "Assisted Hatching",
  subtitle: "Thinning the embryo shell to aid implantation",
  body: {
    procedureName: "Assisted Hatching",
    category: "lab",
    description:
      "A laboratory technique where the embryologist creates a small opening or thins a section of the zona pellucida (the outer shell surrounding the embryo). This may help the embryo 'hatch' out of its shell, a necessary step for implantation into the uterine lining.",
    howItWorks:
      "Using a precision laser, the embryologist creates a small hole or thins a section of the zona pellucida on the day of embryo transfer (or before, for frozen embryos). The laser method is most common and takes seconds. The embryo is not harmed, only the outer shell is affected. The procedure is performed entirely in the lab; the patient is not involved.",
    duration: "Seconds per embryo (lab procedure only)",
    costRange: { low: 500, high: 1500 },
    currency: "USD",
    anesthesia: "none",
    whatToExpect: [
      "Performed by the embryology team, no additional procedure for the patient",
      "Done on the morning of embryo transfer",
      "Your clinic will recommend it based on specific criteria",
      "Does not change the transfer experience in any way",
    ],
    risks: [
      "Very small risk of embryo damage during the procedure",
      "Slightly increased chance of identical twins (very rare)",
    ],
    forWhom: "both",
    frBody: {
      procedureName: "Éclosion assistée",
      description:
        "Technique de laboratoire au cours de laquelle l'embryologiste crée une petite ouverture ou amincit une zone de la zone pellucide (la coque externe entourant l'embryon). Cela peut aider l'embryon à « éclore » et est une étape nécessaire à l'implantation dans la muqueuse utérine.",
      howItWorks:
        "Avec un laser de précision, l'embryologiste crée une petite ouverture ou amincit une zone de la zone pellucide le jour du transfert (ou avant, pour les embryons congelés). La méthode au laser est la plus courante et ne prend que quelques secondes. L'embryon n'est pas affecté, seule la coque externe l'est. La procédure se déroule entièrement au laboratoire ; la patiente n'y participe pas directement.",
      duration: "Quelques secondes par embryon (geste de laboratoire)",
      whatToExpect: [
        "Réalisée par l'équipe d'embryologie, sans geste supplémentaire pour la patiente",
        "Effectuée le matin du transfert",
        "Votre centre la recommandera selon des critères précis",
        "Ne modifie pas le déroulement du transfert",
      ],
      risks: [
        "Risque très faible d'altération embryonnaire pendant la procédure",
        "Légère augmentation du risque de jumeaux identiques (très rare)",
      ],
    },
  },
  sources: [
    {
      title: "Assisted hatching on assisted conception (IVF and ICSI)",
      authors: "Lacey L, Hassan S, Franik S, Seif MW, Akhtar MA",
      journal: "Cochrane Database of Systematic Reviews",
      year: 2021,
      url: "https://pubmed.ncbi.nlm.nih.gov/33730422/",
      evidenceLevel: "meta-analysis",
    },
    {
      title: "Treatment add-ons with limited evidence",
      authors: "Human Fertilisation and Embryology Authority",
      journal: "HFEA",
      year: 2023,
      url: "https://www.hfea.gov.uk/treatments/treatment-add-ons/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(assistedHatching);
